import { useMutation } from '@apollo/client';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';

import { useRef, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { translations } from 'locales/i18n';
import { PermissionModal } from 'app/containers/Articles/ManagePermissionsSettings/components/PermissionsModal';
import { Chip, Fab, Grid, Icon, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import './styles/PermissionItem.scss';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import ContextMenu from 'app/components/ContextMenu';
import { Menu } from '@mui/icons-material';
import { GraphQLErrorHelper } from 'utils/GraphQLErrorHelper';
import DELETE_MUTATION from 'gql/mutations/PermissionProvider/deletePermission.gql';

interface Props {
  permission: any;
}

export const PermissionItem: FunctionComponent<Props> = ({ permission }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const anchorRef = useRef(null);

  const [deletePermission] = useMutation(DELETE_MUTATION);

  const handleDelete = async () => {
    try {
      await deletePermission({
        variables: {
          id: permission.id
        },
        refetchQueries: ['GetPermissions', 'GetRoles']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.managePermissionsSettings.alertsPermission.deleteSuccess())
        )
      );
    } catch (err) {
      const gqlErrorHelper = new GraphQLErrorHelper(err);
      dispatch(
        AlertActions.error(
          t(translations.pages.managePermissionsSettings.alertsPermission.deleteFailed(), {
            cause: gqlErrorHelper.getBackendErrorMessage()
          })
        )
      );
    }
  };

  const renderRecipients = () => {
    return (
      <>
        {permission.recipientRoles.length === 0 ? null : (
          <>
            {permission.recipientRoles.length > 3 ? (
              <Grid container>
                {permission.recipientRoles.slice(0, 3).map(role => (
                  <Chip
                    label={role.name}
                    className="permission__roles__item"
                    icon={<Icon className={role.icon + ' permission__roles__item__icon'} />}
                  />
                ))}
                <Chip
                  label={`+${permission.recipientRoles.length - 3}`}
                  className="permission__roles__item"
                />
              </Grid>
            ) : (
              <Grid container>
                {permission.recipientRoles.map(role => (
                  <Chip
                    label={role.name}
                    className="permission__roles__item"
                    icon={
                      <Icon
                        className={role.icon + ' permission__roles__item__icon'}
                        style={{ width: '30px', marginLeft: '0.5em' }}
                      />
                    }
                  />
                ))}
              </Grid>
            )}
          </>
        )}
      </>
    );
  };

  const ActionButtons = () => {
    return (
      <ContextMenu open={menuOpen} setOpen={setMenuOpen} anchorEl={anchorRef.current}>
        <MenuItem className="ioCourse-item__actions__container">
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setEditModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-edit"
                  style={{
                    width: 'auto',
                    color: 'lightblue'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.edit())} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem className="ioCourse-item__actions__container">
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setDeleteModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-trash-alt"
                  style={{
                    width: 'auto',
                    color: 'red'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.delete())} />
            </Grid>
          </Grid>
        </MenuItem>
      </ContextMenu>
    );
  };

  return (
    <Grid
      container
      className="permission"
      style={{
        backgroundColor: !permission.authorRole.active ? 'lightgrey' : undefined
      }}
    >
      <ErrorBoundary boundaryTag="permissioneDetailBoundary">
        <Modal
          open={editModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setEditModalOpen(false);
          }}
          headline={t(translations.pages.managePermissionsSettings.roleEditing())}
          onCancel={() => {
            setEditModalOpen(false);
          }}
          disabledCancel={disabledCancel}
        >
          <PermissionModal
            permission={{ ...permission }}
            onCancel={() => setEditModalOpen(false)}
            closeHandler={() => {
              setEditModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid container>
        <Grid item xs={1} className="permission__id">
          {permission.id}
        </Grid>
        <Grid item xs={3} className="permission__name">
          <Icon
            className={'permission__name__image ' + permission.authorRole.icon}
            style={{
              color: !permission.authorRole.active ? 'red' : 'black'
            }}
          />
          {permission.authorRole.name}
        </Grid>
        <Grid item xs={7} className="permission__roles">
          {renderRecipients()}
        </Grid>
        <Grid item xs={1} className="permission__actions">
          <Fab size="small" className="action" onClick={() => setMenuOpen(true)} ref={anchorRef}>
            <Menu />
          </Fab>
          <ActionButtons />
        </Grid>
      </Grid>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.managePermissionsSettings.deleteModalPermission.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          handleDelete();
          setDeleteModalOpen(false);
        }}
      >
        <div>{t(translations.pages.managePermissionsSettings.deleteModalPermission.message())}</div>
      </ConfirmationDialog>
    </Grid>
  );
};
